
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocalization } from '../providers/LocalizationProvider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Edit3, Trash2, RefreshCw, Save, X, Package, User as UserIcon, History, Plus, Truck
} from "lucide-react";
import { format } from "date-fns";
import { OrderHistory, Product, User, Order } from "@/api/entities"; // Ensure Order is imported
import { PaymentTypes } from "@/api/client";

export default function OrderDetailsModal({ isOpen, onClose, order, onUpdate, onDelete, onRestore, showDeleted }) {
  const { t, currencySymbol, paymentMethods } = useLocalization(); // локальные ярлыки — fallback
  const [orderHistory, setOrderHistory] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [products, setProducts] = useState([]);
  const [editableOrder, setEditableOrder] = useState(null);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);
  const [viewOrder, setViewOrder] = useState(null);
  const [isRetryingCreateShipmentOrder, setIsRetryingCreateShipmentOrder] = useState(false);
  const [isDeletingOrder, setIsDeletingOrder] = useState(false);
  const [isRestoringOrder, setIsRestoringOrder] = useState(false);
  // Removed paymentMethods and currencySymbol states as they now come from LocalizationProvider

  useEffect(() => {
    if (order && isOpen) {
      loadOrderHistory();
      // Загружаем доступные типы оплат для отображения и редактирования
      PaymentTypes.list().then(setPaymentTypeOptions).catch(() => setPaymentTypeOptions([]));
      // If already in edit mode and order prop changes or modal opens, refresh editable state
      if (isEditing) {
        setEditableOrder(JSON.parse(JSON.stringify(order)));
      }
      // Синхронизируем локальное отображение с пропом
      setViewOrder(order);
    }
    if (!isOpen) {
      setIsEditing(false); // Reset editing state when modal closes
      setEditableOrder(null); // Clear editable order state
    }
  }, [order, isOpen, isEditing]); // Added isEditing to dependency array for correctness

  const loadOrderHistory = async () => {
    if (!order) return;
    try {
      const history = await OrderHistory.get(order.id);
      setOrderHistory(history);
    } catch (error) {
      console.error("Error loading order history:", error);
    }
  };

  const handleEdit = async () => {
    setIsEditing(true);
    setEditableOrder(JSON.parse(JSON.stringify(order))); // Deep copy
    if (products.length === 0) {
      try {
        const productsData = await Product.list();
        setProducts(productsData);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableOrder(null);
  };
  
  const handleFieldChange = (field, value) => {
    setEditableOrder(prev => ({...prev, [field]: value}));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...editableOrder.items];
    const product = products.find(p => p.id === (field === 'product_id' ? value : newItems[index].product_id));

    if (field === 'product_id') {
      newItems[index].product_id = value;
      newItems[index].product_name = product?.name || '';
      newItems[index].unit_price = product?.price || 0;
    }
    
    if (field === 'quantity') {
      newItems[index].quantity = Number(value);
    }
    
    newItems[index].total_price = (newItems[index].unit_price || 0) * (newItems[index].quantity || 0);
    
    updateOrderTotal(newItems);
  };
  
  const addItem = () => {
    const newItem = { product_id: '', product_name: '', quantity: 1, unit_price: 0, total_price: 0 };
    updateOrderTotal([...editableOrder.items, newItem]);
  };

  const removeItem = (index) => {
    const newItems = editableOrder.items.filter((_, i) => i !== index);
    updateOrderTotal(newItems);
  };

  const updateOrderTotal = (items) => {
    const totalAmount = items.reduce((sum, item) => sum + (item.total_price || 0), 0);
    setEditableOrder(prev => ({ ...prev, items, total_amount: totalAmount }));
  };

  const handleSave = async () => {
    setIsUpdating(true);
    const historyRecords = [];
    const user = await User.me();

    // Compare fields and generate history
    if (editableOrder.payment_method !== order.payment_method) {
      const oldLabel = (paymentTypeOptions.find(pt => pt.code === order.payment_method)?.name)
        || (paymentMethods.find(m => m.key === order.payment_method)?.label)
        || (order.payment_method || 'N/A');
      const newLabel = (paymentTypeOptions.find(pt => pt.code === editableOrder.payment_method)?.name)
        || (paymentMethods.find(m => m.key === editableOrder.payment_method)?.label)
        || editableOrder.payment_method;
      historyRecords.push({ order_id: order.id, action: 'field_updated', user_name: user.full_name, old_value: order.payment_method || 'N/A', new_value: editableOrder.payment_method, details: `Метод оплаты изменен с "${oldLabel}" на "${newLabel}"` });
    }
    if (editableOrder.notes !== (order.notes || '')) {
       historyRecords.push({ order_id: order.id, action: 'field_updated', user_name: user.full_name, old_value: order.notes || 'Пусто', new_value: editableOrder.notes, details: `Комментарий к заказу обновлен` });
    }

    // Compare items
    const originalItems = order.items || [];
    const updatedItems = editableOrder.items || [];
    
    updatedItems.forEach(newItem => {
        const oldItem = originalItems.find(i => i.product_id === newItem.product_id);
        if (!oldItem) {
            historyRecords.push({ order_id: order.id, action: 'item_added', user_name: user.full_name, new_value: `${newItem.quantity}x ${newItem.product_name}`, details: `Добавлена позиция: ${newItem.quantity}x ${newItem.product_name}` });
        } else if (oldItem.quantity !== newItem.quantity) {
            historyRecords.push({ order_id: order.id, action: 'item_updated', user_name: user.full_name, old_value: oldItem.quantity, new_value: newItem.quantity, details: `Количество для "${newItem.product_name}" изменено с ${oldItem.quantity} на ${newItem.quantity}` });
        }
    });

    originalItems.forEach(oldItem => {
        if (!updatedItems.find(i => i.product_id === oldItem.product_id)) {
            historyRecords.push({ order_id: order.id, action: 'item_removed', user_name: user.full_name, old_value: `${oldItem.quantity}x ${oldItem.product_name}`, details: `Удалена позиция: ${oldItem.quantity}x ${oldItem.product_name}` });
        }
    });


    try {
      await Order.update(order.id, editableOrder);
      // История для изменения полей (в т.ч. payment_method) уже пишется на бэкенде.
      // На фронте добавим записи только если доступен метод create (bulk отсутствует).
      if (historyRecords.length > 0 && OrderHistory && typeof OrderHistory.create === 'function') {
        await Promise.all(historyRecords.map(rec => OrderHistory.create(rec)));
      }
      // Мгновенно обновляем локальное состояние для отображения без перезагрузки
      setViewOrder(prev => ({ ...(prev || {}), ...editableOrder }));
      await onUpdate(order.id, editableOrder);
      await loadOrderHistory();
    } catch (error) {
        console.error("Failed to save order updates:", error);
    }

    setIsUpdating(false);
    setIsEditing(false);
  };

  const handleRetryCreateShipmentOrder = async () => {
    if (!order) return;
    
    setIsRetryingCreateShipmentOrder(true);
    try {
      const updatedOrder = await Order.retryCreateShipmentOrder(order.id);
      setViewOrder(updatedOrder);
      await onUpdate(order.id, updatedOrder);
      await loadOrderHistory();
    } catch (error) {
      console.error("Failed to retry create shipment order:", error);
      alert(error.message || "Не удалось повторить создание заказа доставки");
    } finally {
      setIsRetryingCreateShipmentOrder(false);
    }
  };

  const handleDeleteClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!order?.id || isDeletingOrder) return;
    if (!window.confirm(t('general.confirm_delete'))) return;

    setIsDeletingOrder(true);
    try {
      await onDelete(order.id);
    } catch (error) {
      console.error("Failed to delete order:", error);
      alert(error.message || "Не удалось удалить заказ");
      setIsDeletingOrder(false);
    }
  };

  const handleRestoreClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!order?.id || isRestoringOrder) return;

    setIsRestoringOrder(true);
    try {
      await onRestore(order.id);
    } catch (error) {
      console.error("Failed to restore order:", error);
      alert(error.message || "Не удалось восстановить заказ");
      setIsRestoringOrder(false);
    }
  };

  if (!order) return null;
  
  const currentOrderData = isEditing ? editableOrder : (viewOrder || order);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className={`text-xl font-bold ${order?.is_deleted ? 'text-red-600' : 'text-slate-900'}`}>
            {t('components.order_details.title', { orderNumber: order?.order_number || order?.id?.slice(-6) })}
            {order?.is_deleted && <Badge className="ml-2 bg-red-100 text-red-800">{t('components.order_details.deleted_badge')}</Badge>}
            <div className="mt-1 text-sm font-normal text-slate-600">
              {order?.customer_name}
              {(() => {
                const sd = order?.shipment_details;
                const addr = sd?.data?.deliveryPoint?.address || sd?.data?.address || order?.shipping_address?.city;
                if (!addr) return null;
                const city = typeof addr === 'string' ? addr.split(',')[0] : addr;
                return <span>{` • ${city}`}</span>;
              })()}
              {order?.payment_method ? <span>{` • ${paymentMethods.find(m => m.key === order.payment_method)?.label || order.payment_method}`}</span> : null}
            </div>
          </DialogTitle>
          <div className="relative z-10 flex items-center gap-2 pr-8">
            {order?.is_deleted ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRestoreClick}
                disabled={isRestoringOrder}
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('general.restore')}
              </Button>
            ) : (
              <>
                {!isEditing ? (
                  <>
                    <Button type="button" variant="outline" size="sm" onClick={handleEdit}>
                      <Edit3 className="w-4 h-4 mr-2" />
                      {t('general.edit')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteClick}
                      disabled={isDeletingOrder}
                      data-testid="order-delete-button"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('general.delete')}
                    </Button>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleCancel}>
                      <X className="w-4 h-4 mr-2" />
                      {t('general.cancel')}
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={isUpdating} className="bg-amber-600 hover:bg-amber-700">
                      <Save className="w-4 h-4 mr-2" />
                      {isUpdating ? t('general.saving') : t('general.save')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogHeader>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">{t('components.order_details.tab_details')}</TabsTrigger>
            <TabsTrigger value="history">{t('components.order_details.tab_history')}</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-slate-600" />
                    {t('components.order_details.order_status')}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('components.order_details.order_date')}</p>
                  <p className="font-semibold text-slate-900">
                    {format(new Date(currentOrderData.created_date), 'MMM d, yyyy')}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('components.order_details.total_amount')}</p>
                  <p className="font-semibold text-slate-900">{currencySymbol}{Number(currentOrderData.total_amount || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('components.order_details.payment_method')}</p>
                  {(isEditing && currentOrderData.status === 'ordered') ? (
                     <Select value={currentOrderData.payment_method || ''} onValueChange={(v) => handleFieldChange('payment_method', v)}>
                          <SelectTrigger><SelectValue placeholder="Select method"/></SelectTrigger>
                          <SelectContent>
                             {(paymentTypeOptions.length > 0
                               ? paymentTypeOptions.map(type => (
                                   <SelectItem key={type.code} value={type.code}>{type.name}</SelectItem>
                                 ))
                               : paymentMethods
                                   .filter(m => m.is_active !== false)
                                   .map(method => (
                                     <SelectItem key={method.key} value={method.key}>{method.label}</SelectItem>
                                   ))
                             )}
                          </SelectContent>
                      </Select>
                   ) : (
                     <p className="font-semibold text-slate-900">
                       {paymentTypeOptions.find(pt => pt.code === currentOrderData.payment_method)?.name
                         || paymentMethods.find(m => m.key === currentOrderData.payment_method)?.label
                         || currentOrderData.payment_method
                         || 'N/A'}
                     </p>
                   )}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('components.order_details.total_items')}</p>
                  <p className="font-semibold text-slate-900">
                    {currentOrderData.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-slate-600" />
                  {t('components.order_details.customer_info')}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('components.order_details.name')}</p>
                  <p className="font-semibold text-slate-900">{currentOrderData.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('components.order_details.email')}</p>
                  <p className="font-semibold text-slate-900">{currentOrderData.customer_email}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-4 flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-slate-600" />
                  {t('components.order_details.order_items')}
                </CardTitle>
                {isEditing && (
                    <Button type="button" size="sm" variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-2" /> Добавить</Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {currentOrderData.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      {isEditing ? (
                        <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                            <div className="col-span-6">
                                <Select value={item.product_id} onValueChange={(v) => handleItemChange(index, 'product_id', v)}>
                                    <SelectTrigger><SelectValue placeholder="Select product"/></SelectTrigger>
                                    <SelectContent>
                                        {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-2">
                               <Input type="number" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} />
                            </div>
                            <div className="col-span-3 text-right">
                                <p className="font-semibold text-slate-900">{currencySymbol}{Number(item.total_price || 0).toFixed(2)}</p>
                            </div>
                            <div className="col-span-1">
                                <Button type="button" size="icon" variant="ghost" onClick={() => removeItem(index)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                            </div>
                        </div>
                      ) : (
                        <>
                          <div>
                            <p className="font-medium text-slate-900">{item.product_name}</p>
                            <p className="text-sm text-slate-600">Qty: {item.quantity}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-slate-900">{currencySymbol}{Number(item.total_price || 0).toFixed(2)}</p>
                            <p className="text-sm text-slate-600">{currencySymbol}{Number(item.unit_price || 0).toFixed(2)} each</p>
                          </div>
                        </>
                      )}
                    </div>
                  )) || <p className="text-slate-500">No items found</p>}
                </div>
              </CardContent>
            </Card>

            {/* Shipping Information */}
            {(currentOrderData.shipping_address || currentOrderData.shipment_details) && (
              <Card className="border-slate-200">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-slate-600" />
                      {t('components.order_details.shipping_info')}
                    </div>
                    {currentOrderData.shipment_details?.status === 'metaship_order_creation_failed' && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="text-red-600 cursor-help"
                          title={t('delivery_status.tooltip.metaship_order_is_failed')}
                        >
                          <Truck className="w-5 h-5" />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-8 h-8"
                          onClick={handleRetryCreateShipmentOrder}
                          disabled={isRetryingCreateShipmentOrder}
                          title={t('components.order_details.retry_shipment_order')}
                        >
                          <RefreshCw className={`w-4 h-4 ${isRetryingCreateShipmentOrder ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Shipment Details */}
                  {currentOrderData.shipment_details && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-green-800">
                          {currentOrderData.shipment_details.type === 'self_pickup' && 'Self Pickup'}
                          {currentOrderData.shipment_details.type === 'cdek_delivery_point' && 'CDEK Delivery Point'}
                          {currentOrderData.shipment_details.type === 'cdek_courier' && 'CDEK Courier'}
                          {currentOrderData.shipment_details.type === 'deprecated_address' && 'Legacy Address'}
                        </span>
                      </div>
                      
                      {currentOrderData.shipment_details.data?.address && (
                        <div>
                          <p className="text-sm font-medium text-slate-500">Address</p>
                          <p className="font-semibold text-slate-900">{currentOrderData.shipment_details.data.address}</p>
                        </div>
                      )}
                      
                      {currentOrderData.shipment_details.data?.deliveryPoint && (
                        <div>
                          <p className="text-sm font-medium text-slate-500">Delivery Point</p>
                          <p className="font-semibold text-slate-900">{currentOrderData.shipment_details.data.deliveryPoint.name}</p>
                          {currentOrderData.shipment_details.data.deliveryPoint.address && (
                            <p className="text-sm text-slate-600">{currentOrderData.shipment_details.data.deliveryPoint.address}</p>
                          )}
                        </div>
                      )}
                      
                      <div>
                        <p className="text-sm font-medium text-slate-500">Delivery Cost</p>
                        <p className="font-semibold text-slate-900">
                          {currencySymbol}{Number(currentOrderData.shipment_details.data?.price || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ) || null}
                  
                  {/* Shipping Cost */}
                  {currentOrderData.shipping_cost && !currentOrderData.shipment_details && (
                    <div>
                      <p className="text-sm font-medium text-slate-500">Shipping Cost</p>
                      <p className="font-semibold text-slate-900">
                        {currencySymbol}{Number(currentOrderData.shipping_cost).toFixed(2)}
                      </p>
                    </div>
                  ) || null}
                </CardContent>
              </Card>
            )}
            
            <Card className="border-slate-200">
                <CardHeader>
                    <CardTitle className="text-lg">{t('components.order_details.order_notes')}</CardTitle>
                </CardHeader>
                <CardContent>
                    {isEditing ? (
                        <Textarea value={currentOrderData.notes || ''} onChange={(e) => handleFieldChange('notes', e.target.value)} className="h-24"/>
                    ) : (
                        <p className="text-slate-600">{currentOrderData.notes || 'Нет комментариев.'}</p>
                    )}
                </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-600" />
                  {t('components.order_details.tab_history')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {orderHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">{t('components.order_details.history_empty_title')}</p>
                    <p className="text-sm">{t('components.order_details.history_empty_desc')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orderHistory.map((h, index) => {
                      const actionLabels = {
                        created: 'Создание заказа',
                        updated: 'Обновление заказа',
                        status_changed: 'Смена статуса',
                        deleted: 'Удаление заказа',
                        restored: 'Восстановление заказа',
                        payment_initiated: 'Инициация платежа',
                        payment_status_updated: 'Обновление статуса платежа',
                        payment_succeeded: 'Оплата успешна',
                        payment_failed: 'Оплата неуспешна',
                        payment_refunded: 'Возврат оплаты',
                        item_added: 'Добавлена позиция',
                        item_removed: 'Удалена позиция',
                        item_updated: 'Изменена позиция',
                        field_updated: 'Изменение поля',
                      };

                      let detailsNode = null;
                      if (h.old_value !== null || h.new_value !== null) {
                        detailsNode = (
                          <div className="text-sm text-slate-700">
                            <span className="font-medium">Было:</span> {String(h.old_value ?? '—')}
                            <span className="mx-2">→</span>
                            <span className="font-medium">Стало:</span> {String(h.new_value ?? '—')}
                          </div>
                        );
                      } else if (h.details) {
                        try {
                          const dj = JSON.parse(h.details);
                          const kv = [];
                          if (dj.provider) kv.push(`Провайдер: ${dj.provider}`);
                          if (dj.method) kv.push(`Метод: ${dj.method}`);
                          if (dj.paymentId) kv.push(`ID платежа: ${dj.paymentId}`);
                          if (dj.amount !== undefined) kv.push(`Сумма: ${dj.amount}`);
                          if (dj.tbankStatus) kv.push(`Статус T‑Bank: ${dj.tbankStatus}`);
                          if (dj.source) kv.push(`Источник: ${dj.source}`);
                          detailsNode = (
                            <div className="text-sm text-slate-700">{kv.join(' • ') || h.details}</div>
                          );
                        } catch (_) {
                          detailsNode = <div className="text-sm text-slate-700">{h.details}</div>;
                        }
                      }

                      return (
                        <div key={index} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-medium text-slate-900">{actionLabels[h.action] || h.action}</div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                {(h.created_by || 'system')} • {format(new Date(h.created_date), 'dd.MM.yyyy HH:mm')}
                              </div>
                            </div>
                          </div>
                          {detailsNode && <div className="mt-2">{detailsNode}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
